import React, { useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import TestInfo from '../../../../../../../../Models/TestInfo';
import { TestSelectionFormValues } from '../../../../../../SelectTestsStep';
import usePreviewQuestions, { PreviewQuestionsProps } from '../PreviewQuestions/Dialog/usePreviewQuestions';
import BaseTestCardContent from './components/BaseTestCardContent';
import BaseTestCardHeader from './components/BaseTestCardHeader';
import getFieldNameByTestType from './utils/getFieldNameByTestType';
import { cn } from '@/lib/utils';

export interface BaseTestCardProps extends TestInfo, PreviewQuestionsProps {
    testName: string;
    fieldName: string;
    imageURL: string;
    className?: string;
}

function BaseTestCard({
    imageURL,
    testType,
    topics,
    totalTopicsCount,
    duration,
    testName,
    fieldName: fieldNameTestType,
    demoQuestions,
    likertOptions,
    className,
}: BaseTestCardProps): React.JSX.Element {
    const { setValue, control } = useFormContext<TestSelectionFormValues>();
    const fieldName = useMemo(() => getFieldNameByTestType(testType), [testType]);
    const checked = useWatch({ control, name: fieldName });
    const { dialog, toggleDialog, isPreviewQuestionsLoading } = usePreviewQuestions({
        testType,
        demoQuestions,
        likertOptions,
    });

    const [active, setActive] = useState(false);

    const isInteractive = checked || active;

    const handleMouseEnter = () => setActive(true);
    const handleMouseLeave = () => setActive(false);

    const handleClick = () => {
        setValue(fieldName as keyof TestSelectionFormValues, !checked);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
        }
    };

    return (
        <>
            {dialog}
            <div
                id={fieldNameTestType}
                tabIndex={0}
                role="button"
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                className={cn(
                    'test-card',
                    'border-border mx-auto max-h-107 min-h-107 max-w-69 min-w-69 cursor-pointer rounded-lg border transition-shadow',
                    'col-start-auto row-span-3 row-start-auto grid grid-rows-subgrid',
                    checked ? 'bg-card' : 'bg-muted',
                    isInteractive ? 'shadow-lg' : 'shadow-xs',
                    className,
                )}
            >
                <BaseTestCardHeader testName={testName} fieldName={fieldName} checked={checked} />
                <img src={imageURL} title="test image" alt={testName} className="row-span-1 size-full object-cover" />
                <BaseTestCardContent
                    testType={testType}
                    topics={topics}
                    totalTopicsCount={totalTopicsCount}
                    duration={duration}
                    isPreviewQuestionsLoading={isPreviewQuestionsLoading}
                    toggleDialog={toggleDialog}
                />
            </div>
        </>
    );
}

export default BaseTestCard;
